"use client";

import { useSyncExternalStore } from "react";
import { ArrowUp } from "lucide-react";
import { cn, scrollToTop } from "@/lib/utils";
import { MOBILE_BOTTOM_TOOL_DOCK_HEIGHT } from "@/components/mobile-bottom-tools/constants";

// 이 스크롤(px) 이상 내려가면 버튼을 노출한다. 값이 페이지의 총 스크롤 가능 거리보다 크면
// 버튼이 영영 안 뜨므로(예: 비프리미엄 모의고사 CTA 카드는 최대 스크롤이 ~300px), 뷰포트보다
// 작은 값으로 잡아 살짝만 긴 페이지(모의고사 안내 카드 포함)에서도 노출되게 한다.
const SCROLL_THRESHOLD_PX = 200;

// set-state-in-effect 없이 window 스크롤을 구독한다(JCG-2035 패턴, AttendanceFloatingButton·useViewport 참고).
// getSnapshot 은 boolean(임계값 초과 여부)만 반환하므로 임계값을 넘나들 때만 리렌더된다.
function subscribeScroll(callback: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("scroll", callback, { passive: true });
  return () => window.removeEventListener("scroll", callback);
}

function getScrolledPastSnapshot(): boolean {
  return typeof window !== "undefined" && window.scrollY > SCROLL_THRESHOLD_PX;
}

// SSR/hydration 첫 렌더는 항상 false(숨김) → 서버 HTML 과 클라이언트 첫 렌더 일치(mismatch 없음).
function getServerSnapshot(): boolean {
  return false;
}

/**
 * 세로로 긴 콘텐츠 페이지(글·코딩·퀴즈·모의고사)의 공통 "맨 위로" 플로팅 버튼(JCG-2973).
 *
 * - `PostLayout` 하단에 렌더되어 theory/coding/past-exam 회차를 커버하고, `PostLayout`을
 *   안 거치는 `/mock-exam`·`/final-mock-exam` 페이지에는 각 page.tsx 에서 직접 렌더한다.
 * - 셸(3컬럼)/클래식 데스크탑/모바일 세 레이아웃 모두 본문이 window 스크롤이므로
 *   `window.scrollTo({ top: 0 })` 단일 경로로 동작한다.
 * - 배치(z-40, 모달 z-50 아래): 모바일은 우측 하단 dock(68px+여백+safe-area) 위로 띄우고,
 *   데스크탑 셸(lg~)은 우측 사이드바를 덮지 않도록 본문 콘텐츠 영역 우측 하단(`--right-w` 기준)에 둔다.
 * - 스크롤 200px 초과 시 노출하며, 모든 사용자(무료 포함)에게 노출한다 — 이용권 게이트 없음.
 */
export default function ScrollToTopButton() {
  const show = useSyncExternalStore(
    subscribeScroll,
    getScrolledPastSnapshot,
    getServerSnapshot,
  );

  return (
    // 숨김 상태(show=false)는 aria-hidden 이 아니라 inert 로 표현한다(JCG-2973 a11y 회귀 수정):
    // inert 는 포커스·포인터를 차단하고 AT 트리에서 제외하며, 엘리먼트가 inert 가 되는 순간
    // 브라우저가 포커스를 자동 해제한다 → 마우스로 누른 직후 스크롤이 임계값 아래로 내려가
    // 같은 프레임에 버튼이 숨겨져도 "포커스 보유 엘리먼트에 aria-hidden" 위반 경고가 나지 않는다.
    // inert 가 focus/pointer 를 이미 막으므로 기존 tabIndex 분기는 제거했다(중복). (React 19 boolean prop)
    <button
      type="button"
      // 방어적 blur: 클릭 프레임에 포커스를 쥔 채 곧바로 inert 가 걸리는 경합을 한 번 더 차단.
      onClick={(e) => {
        e.currentTarget.blur();
        scrollToTop();
      }}
      aria-label="맨 위로"
      title="맨 위로"
      inert={!show}
      style={
        {
          "--mobile-bottom-tool-dock-height": MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
        } as React.CSSProperties
      }
      className={cn(
        // 모바일/클래식: 우측 하단 고정, 하단 dock 위로 오프셋.
        "fixed right-4 z-40",
        "bottom-[calc(var(--mobile-bottom-tool-dock-height,68px)+1rem+env(safe-area-inset-bottom))] lg:bottom-4",
        // 데스크탑 셸(lg~): 우측 사이드바(필기·출석 등) 위를 덮지 않도록 본문 콘텐츠 영역 우측 하단으로.
        // --right-w = 우측 컬럼 폭(ShellFrame 인라인 변수, FAB 이 상속) → main 콘텐츠 오른쪽 끝 -1rem 위치.
        // 셸이 아닌 페이지(--right-w 미정의)는 0px 폴백 → 우측 하단(right-4)으로 안전 degrade.
        "lg:right-[calc(var(--right-w,0px)+1rem)]",
        // 화살표 + "TOP" 글자 세로 스택. 48px 원형 — 모바일 최소 터치 타깃(패턴 §4) 충족.
        // touch-manipulation 으로 더블탭 지연 제거.
        "inline-flex h-12 w-12 flex-col items-center justify-center gap-0.5 rounded-full touch-manipulation",
        // 중립 토큰 표면(라이트/다크 자동 대응, dark: 페어 불필요) + 살짝 반투명 + blur 로 떠 있는 느낌.
        "border border-border bg-card/90 text-muted-foreground shadow-lg backdrop-blur",
        "transition-[opacity,transform,color,background-color,box-shadow] duration-200 ease-out",
        // 브랜드 강조는 hover/focus 에만 절제해서 — amber 아이콘 틴트(dark 페어) + amber 포커스 링(프로젝트 관례).
        "hover:bg-accent hover:text-amber-600 hover:shadow-xl dark:hover:text-amber-400",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "motion-reduce:transition-none",
        // 등장/사라짐: 페이드 + 살짝 슬라이드. 숨김 시 포커스/클릭 불가.
        show
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-1.5 opacity-0",
      )}
    >
      <ArrowUp className="h-4 w-4" strokeWidth={2.5} aria-hidden />
      {/* 시각적 라벨 — 접근성 이름은 aria-label("맨 위로")이 담당하므로 장식 처리 */}
      <span className="text-[10px] font-bold leading-none tracking-wide" aria-hidden>
        TOP
      </span>
    </button>
  );
}
